import {useCallback, useContext, useEffect, useRef, useState} from 'react';
import {view} from '@sail/ui/deprecated';
import {Profile} from '@stripe-internal/conversations-shared-dependencies';
import {ConfigContext} from '../../Providers/ConfigProvider';
import {useAnalytics} from '../../helpers/analytics';
import {
  ParentMessageBusContext,
  MessageToParentType,
} from '../../Providers/ParentMessageBusProvider';
import {LoadingScreen} from '../LoadingScreen';
import {UnrecoverableErrorScreen} from '../UnrecoverableErrorScreen';
import {useApi} from '../../Providers/ApiProvider/ApiProvider';

type Props = {
  onSuccess: (profile: Profile) => void;
  onError: (error?: string) => void;
  sentryEventId?: string;
};

const RETRY_INTERVAL_MS = 1000;
const RETRY_COUNT = 10;

const ERROR_GIVE_UP = 'session_required_but_session_never_came';

export function SessionRetry({onSuccess, onError, sentryEventId}: Props) {
  const {
    audience,
    entrypoint: entryPoint,
    livemode,
    requiresSession,
    surfaceLoadId,
    expectedMerchant,
    expectedUser,
  } = useContext(ConfigContext);
  const analytics = useAnalytics();
  const {fetchProfile} = useApi();

  const fetching = useRef<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [attemptNumber, setAttemptNumber] = useState<number>(0);

  const {post} = useContext(ParentMessageBusContext);
  const requestOpenEmailModal = useCallback(() => {
    post({
      type: MessageToParentType.RequestOpenEmailModal,
      data: {},
    });
  }, [post]);

  const retry = useCallback(() => {
    fetchProfile({
      audience,
      entryPoint: entryPoint ?? 'dashboard',
      requiresSession: !!requiresSession,
      livemode,
      attemptNumber,
      surfaceLoadId,
      expectedMerchant,
      expectedUser,
    })
      .then((data) => {
        fetching.current = false;

        if ('profile' in data) {
          onSuccess(data.profile);
          return;
        } else if (data.error === ERROR_GIVE_UP) {
          setError(true);
          onError();
          return;
        }

        setAttemptNumber(attemptNumber + 1);
      })
      .catch(() => {
        fetching.current = false;
        setAttemptNumber(attemptNumber + 1);
      });

    analytics.track('support_conversations_widget_session_retry', {
      audience,
      entrypoint: entryPoint,
    });
  }, [
    analytics,
    attemptNumber,
    audience,
    entryPoint,
    livemode,
    onError,
    onSuccess,
    requiresSession,
    surfaceLoadId,
    expectedMerchant,
    expectedUser,
    fetchProfile,
  ]);

  useEffect(() => {
    if (fetching.current) {
      return;
    }

    if (attemptNumber > RETRY_COUNT) {
      setError(true);
      onError();
      return;
    }

    fetching.current = true;

    setTimeout(retry, RETRY_INTERVAL_MS);
  }, [attemptNumber, error, onError, retry]);

  if (!error) {
    return (
      <view.div data-testid="session-retry-screen">
        <LoadingScreen />
      </view.div>
    );
  }

  // If we are in dashboard, we want to allow them to open the local
  // dashboard modal instead of linking to another surface that also may not
  // have a session.
  const contactClickHandler =
    entryPoint === 'dashboard' ? requestOpenEmailModal : undefined;

  return (
    <view.div data-testid="session-retry-screen">
      <UnrecoverableErrorScreen
        onContactClick={contactClickHandler}
        includeSignInSuggestion
        sentryEventId={sentryEventId}
      />
    </view.div>
  );
}
